import { primarySession } from "@/lib/sessions";

const num = (v) => Number(v) || 0;

function bestBy(groups, val) {
  const rows = Object.entries(groups).map(([k, list]) => ({ k, n: list.length, v: list.reduce((s, e) => s + val(e), 0) }));
  if (!rows.length) return { best: null, worst: null };
  return {
    best: rows.reduce((a, b) => (b.v > a.v ? b : a)),
    worst: rows.reduce((a, b) => (b.v < a.v ? b : a)),
  };
}

function group(list, keyFn) {
  return list.reduce((acc, e) => {
    const k = keyFn(e);
    if (k != null) (acc[k] ||= []).push(e);
    return acc;
  }, {});
}

export function computeReview(entries, { period, mode, badges = [], plans = [] }) {
  const val = mode === "r" ? (e) => num(e.r_multiple) : (e) => num(e.pnl);
  const list = entries
    .filter((e) => (e.traded_on || "").startsWith(period))
    .sort((a, b) => `${a.traded_on}${a.entry_time || ""}`.localeCompare(`${b.traded_on}${b.entry_time || ""}`));
  const wins = list.filter((e) => val(e) > 0).length;
  let streak = 0, run = 0;
  list.forEach((e) => { run = val(e) > 0 ? run + 1 : 0; streak = Math.max(streak, run); });
  const days = group(list, (e) => e.traded_on);
  const dayVals = Object.values(days).map((d) => d.reduce((s, e) => s + val(e), 0));
  const dayEntries = Object.entries(days).map(([k, d]) => ({ k, v: d.reduce((s, e) => s + val(e), 0) }));
  const bestDay = dayEntries.length ? dayEntries.reduce((a, b) => (b.v > a.v ? b : a)) : null;
  const months = bestBy(group(list, (e) => Number(e.traded_on.slice(5, 7)) - 1), val);
  const weekdays = bestBy(group(list, (e) => new Date(`${e.traded_on}T12:00:00`).getDay()), val);
  const sessions = bestBy(group(list.filter((e) => e.entry_time), (e) => primarySession(new Date(`${e.traded_on}T${e.entry_time}`))), val);
  const instruments = group(list, (e) => (e.instrument || "").toUpperCase() || null);
  const top = Object.entries(instruments).sort((a, b) => b[1].length - a[1].length)[0];
  const scores = list.map((e) => e.grader_data?.score).filter((s) => typeof s === "number");
  const grades = list.map((e) => e.grader_data?.grade).filter(Boolean);
  const gradeCounts = group(grades, (g) => g);
  return {
    list,
    total: list.reduce((s, e) => s + val(e), 0),
    trades: list.length,
    winRate: list.length ? Math.round((wins / list.length) * 100) : 0,
    greenDays: dayVals.filter((v) => v > 0).length,
    tradingDays: dayVals.length,
    streak,
    bestDay,
    bestMonth: months.best,
    worstMonth: months.worst,
    bestWeekday: weekdays.best,
    bestSession: sessions.best,
    topInstrument: top ? { k: top[0], n: top[1].length, v: top[1].reduce((s, e) => s + val(e), 0) } : null,
    avgScore: scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null,
    topGrade: Object.entries(gradeCounts).sort((a, b) => b[1].length - a[1].length)[0]?.[0] || null,
    graded: scores.length,
    climbTrades: list.filter((e) => e.climb_plan_id).length,
    plans: plans.filter((p) => (p.created_at || "").startsWith(period)).length,
    badges: badges.filter((b) => (b.earned_at || "").startsWith(period)),
    dayMap: Object.fromEntries(Object.entries(days).map(([k, d]) => [k, d.reduce((s, e) => s + val(e), 0)])),
  };
}

export function fmtValue(n, mode) {
  if (mode === "hide") return null;
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  if (mode === "r") return `${sign}${Math.abs(n).toFixed(1)}R`;
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function prevMonth(ym) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function monthTotal(entries, ym, mode) {
  const val = mode === "r" ? (e) => num(e.r_multiple) : (e) => num(e.pnl);
  const list = entries.filter((e) => (e.traded_on || "").startsWith(ym));
  return list.length ? list.reduce((s, e) => s + val(e), 0) : null;
}
